import { WhiteBox, ComparisonBlock } from "@foodpilot/foods";
import { Grid2, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Fragment } from "react/jsx-runtime";
import { DistributionChartCore } from "src/components/ScoreDetail/Chart/Distribution/DistributionChartCore";
import { DiagnosticProps } from "../diagnostic.types";
import { DiagnosticActions } from "./DiagnosticActions";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { OlapLocalizedLabelService } from "src/context/OlapScores/OlapLocalizedLabelService";
import { OlapHeadingColorService } from "src/context/OlapScores/OlapHeadingColorService";
import { useGetOneSsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";
import { Loading } from "src/components/utils/Loading.tsx";

export const DiagnosticInner = (props: DiagnosticProps) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const scores = props.diagnostic?.post?.scores;
    const prevCampaign = props.diagnostic?.post?.prevCampaign;
    const prevCampaignTotal = OlapScoresService.normalizeNumericalScore(prevCampaign?.value);
    const prevCampaignScores =
        prevCampaignTotal !== null && scores ?
            {
                ...scores,
                score: {
                    ...scores.score,
                    value: prevCampaignTotal,
                },
            }
        :   undefined;

    const currentTotal = OlapScoresService.normalizeNumericalScore(scores?.score.value);
    const sectorScoreTotal = OlapScoresService.normalizeNumericalScore(scores?.score.avg);
    const scoreCount = OlapScoresService.normalizeNumericalScore(scores?.score.count) ?? 0;
    const scoreImprovement =
        props.scoreDetail?.improvement ?? (props.score.displayMode === "carbon" ? "decrease" : "increase");

    const ssqId = props.diagnostic?.id ?? -99;
    const completion = props.diagnostic?.post?.completion ?? 0;
    const isLocked = props.diagnostic?.post?.isLocked;
    const isLockedOrCompleted = isLocked || completion === 100;

    const { ssqVersion, isReady: isSsqVersionReady } = useGetOneSsqVersion(props.diagnostic?.post?.ssqVersionId);

    if (!isSsqVersionReady) {
        return <Loading />;
    }

    return (
        <Fragment>
            <DiagnosticActions ssqId={ssqId} scoreId={props.scoreId} isComplete={isLockedOrCompleted} />
            <WhiteBox>
                <DistributionChartCore
                    scoreId={Number(props.scoreId)}
                    olapScores={scores}
                    olapPrevScores={prevCampaignScores}
                    prevCampaign={prevCampaign?.year}
                    getDimensionValueLocalizedLabel={(dimension: string, key: string | number): string | null => {
                        return OlapLocalizedLabelService.localize(dimension, key, { ssqVersion });
                    }}
                    getDimensionValuesColors={(dimensionValues) =>
                        OlapHeadingColorService.resolveMany(dimensionValues, { ssqVersion })
                    }
                    DoughnutPropsOverride={{
                        showTooltip: false,
                    }}
                />
            </WhiteBox>
            <Grid2 container spacing={theme.spacing(2)}>
                {/* This 5 may need to be defined somewhere. Idk... I put it here to ease back-end */}
                {currentTotal && sectorScoreTotal && scoreCount > 5 && (
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <WhiteBox padding={theme.spacing(2)}>
                            <ComparisonBlock
                                currentValue={currentTotal}
                                comparedTo={{
                                    value: sectorScoreTotal,
                                    unit: props.score.unit,
                                    label: "",
                                }}
                                comparisonText={t(
                                    "company.dashboard.sector_comparison",
                                    "compared to the average for my sector",
                                )}
                                improvement={scoreImprovement}
                                layout={{
                                    direction: "horizontal-2-rows",
                                    size: "M",
                                }}
                                mode="difference"
                            />
                        </WhiteBox>
                    </Grid2>
                )}
            </Grid2>
        </Fragment>
    );
};
